/**
 * PostTrialExpiredState — shown when a trial ended without a payment
 * method and Stripe flipped the subscription to ``incomplete_expired``.
 *
 * Replaces both the subscription detail view (which would otherwise show
 * stale "Trial ends on X" copy after the date has passed) and any
 * subscription-action affordances. The single CTA scrolls the user back
 * to the plan picker so they can resubscribe with a payment method.
 */

import { AlertTriangle } from '@untitledui/icons';
import { useCallback } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { FeaturedIcon } from '@/lib/base/foundations/featured-icon/featured-icon';

interface PostTrialExpiredStateProps {
  /** Scrolls / routes back to the PlanPickerSection. */
  onChoosePlan: () => void;
}

export function PostTrialExpiredState({
  onChoosePlan,
}: PostTrialExpiredStateProps) {
  // Wrapped useCallback so the Button's onPress reference is stable
  // across renders (preventing react-aria from re-attaching listeners).
  const handleChoosePlan = useCallback(() => {
    onChoosePlan();
  }, [onChoosePlan]);

  return (
    <div
      className='bg-bg-primary rounded-xl border border-border-secondary shadow-sm p-8'
      data-component='billing-banner'
      data-instance='trial-expired'
    >
      <div className='flex flex-col items-center text-center'>
        <FeaturedIcon
          icon={AlertTriangle}
          color='warning'
          theme='modern'
          size='lg'
          className='mb-4'
        />
        <h2 className='text-2xl font-semibold text-primary mb-2'>
          Your trial has ended
        </h2>
        <p className='text-sm text-tertiary mb-6 max-w-md'>
          The trial period ended without a payment method on file. Subscribe to
          a plan below to restore access &mdash; your data and settings are
          still here, ready to pick up where you left off.
        </p>
        <Button color='primary' size='md' onClick={handleChoosePlan}>
          Choose a plan
        </Button>
      </div>
    </div>
  );
}
